/**
 * Base API Client
 * Mirrors Laravel's base Controller pattern
 */

import axios, { type AxiosInstance, type AxiosError, type AxiosRequestConfig } from 'axios'
import { ensureXsrfCookie, readXsrfCookie, refreshXsrfCookie } from '@/lib/csrf'
import type { ApiError } from '@/types'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

export class BaseApiClient {
  protected client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      withCredentials: true, // Enable Sanctum cookie-based auth
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })

    // Request interceptor to add CSRF token header (required by Laravel Sanctum)
    this.client.interceptors.request.use(
      (config) => {
        const csrfToken = readXsrfCookie()
        if (csrfToken) {
          config.headers['X-XSRF-TOKEN'] = csrfToken
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor for centralized error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const errorCode = error.response?.data?.code

        // 419 CSRF mismatch: refresh the token and retry once (mirrors
        // ApiClient — the retried request re-enters the request interceptor,
        // which stamps the fresh cookie into X-XSRF-TOKEN).
        if (error.response?.status === 419 && error.config && !error.config._csrfRetry) {
          error.config._csrfRetry = true
          try {
            await refreshXsrfCookie()
          } catch {
            // Refresh failed — retrying would 419 again with the same token.
            return Promise.reject(this.normalizeError(error))
          }
          return this.client.request(error.config)
        }

        // Only redirect to login if truly unauthenticated (not just missing license)
        if (error.response?.status === 401 && errorCode === 'unauthenticated') {
          // Prevent redirect loop - don't redirect if already on login page
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login'
          }
        }

        // For 403 errors (no licenses, access denied), let the UI handle it
        return Promise.reject(this.normalizeError(error))
      }
    )
  }

  public setActiveLicense(licenseId: number, domain?: string): void {
    this.client.defaults.headers.common['X-Active-License-Id'] = licenseId.toString()
    if (domain) {
      this.client.defaults.headers.common['X-Site-Domain'] = domain
    }
  }

  protected normalizeError(error: AxiosError<ApiError>): ApiError {
    if (error.response) {
      return {
        message: error.response.data.message || 'An error occurred',
        errors: error.response.data.errors,
        code: error.response.data.code,
        status: error.response.status,
      }
    }
    return {
      message: error.message || 'Network error',
      status: 0,
    }
  }

  // Generic CRUD methods matching Laravel patterns
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config)
    return response.data
  }

  protected async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    await ensureXsrfCookie()
    const response = await this.client.post<T>(url, data, config)
    return response.data
  }

  protected async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    await ensureXsrfCookie()
    const response = await this.client.put<T>(url, data, config)
    return response.data
  }

  protected async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    await ensureXsrfCookie()
    const response = await this.client.patch<T>(url, data, config)
    return response.data
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    await ensureXsrfCookie()
    const response = await this.client.delete<T>(url, config)
    return response.data
  }

  public clearActiveLicense(): void {
    delete this.client.defaults.headers.common['X-Active-License-Id']
    delete this.client.defaults.headers.common['X-Site-Domain']
  }
}

// Export singleton instance (like Laravel's Service Container)
export const baseApiClient = new BaseApiClient()
